import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import IconArrowLeft from "@/app/(site)/_components/Atoms/Icons/IconArrowLeft"
import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Text from "@/app/(site)/_components/Atoms/Text"
import { Media } from "@/lib/content-service/types/ContentServiceProduct"
import React, { useEffect, useState } from "react"

type FullscreenImagePopupProps = {
    media: Media
    selectedImageIndex: number
    onClose: () => void
    productName: string
}

// Helper function to determine the correct image source
const getImageSrc = (
    width: number,
    media: {
        big: { url: string }[]
        small: { url: string }[]
        standard: { url: string }[]
    },
    selectedIndex: number
): string => {
    if (!media) return "" // Ensure media is valid
    if (width <= 500) {
        return media.small?.[selectedIndex]?.url || media.small?.[0]?.url || ""
    } else if (width <= 1280) {
        return media.standard?.[selectedIndex]?.url || media.standard?.[0]?.url || ""
    } else {
        return media.big?.[selectedIndex]?.url || media.big?.[0]?.url || ""
    }
}

const FullscreenImagePopup: React.FC<FullscreenImagePopupProps> = ({
    media,
    selectedImageIndex,
    productName,
    onClose
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(selectedImageIndex)
    const [imageSrc, setImageSrc] = useState(() =>
        getImageSrc(
            1281, // always use the big image
            media,
            selectedImageIndex
        )
    )

    const updateImageSrc = () => {
        setImageSrc(getImageSrc(1281, media, currentImageIndex))
    }

    useEffect(() => {
        updateImageSrc()
        const handleResize = () => updateImageSrc()
        window.addEventListener("resize", handleResize)

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" || e.key === "Backspace") {
                onClose()
            } else if (e.key === "ArrowLeft") {
                goToPrevious()
            } else if (e.key === "ArrowRight") {
                goToNext()
            }
        }

        window.addEventListener("keydown", handleKeyDown)
        return () => {
            window.removeEventListener("keydown", handleKeyDown)
            window.removeEventListener("resize", handleResize)
        }
    }, [currentImageIndex, onClose])

    const goToPrevious = () =>
        setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : media.big.length - 1))
    const goToNext = () =>
        setCurrentImageIndex(prev => (prev < media.big.length - 1 ? prev + 1 : 0))

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
            <Text
                size="small"
                weight="regular"
                className="absolute left-16 top-16 z-50 md:left-20 md:top-24"
            >
                {productName}
            </Text>
            <Text
                size="small"
                weight="regular"
                className="absolute left-16 top-32 z-50 md:left-20 md:top-44"
            >
                {`${currentImageIndex + 1} / ${media.big.length}`}
            </Text>
            <div className="relative flex h-full w-full items-center justify-center">
                <button
                    onClick={onClose}
                    className="absolute right-16 top-16 z-50 m-4 md:right-24 md:top-24"
                >
                    <IconClose className="h-32 w-32 text-black" />
                </button>

                {imageSrc && (
                    <BaseImage
                        src={imageSrc}
                        alt={`image ${currentImageIndex}`}
                        fill
                        className="h-full max-h-full w-full max-w-full border object-contain"
                    />
                )}

                <button
                    onClick={goToPrevious}
                    className="absolute left-16 top-1/2 z-50 -translate-y-1/2 transform md:left-24"
                >
                    <IconArrowLeft className="h-32 w-32 text-black" />
                </button>
                <button
                    onClick={goToNext}
                    className="absolute right-16 top-1/2 z-50 -translate-y-1/2 transform md:right-24"
                >
                    <IconArrowRight className="h-32 w-32 text-black" />
                </button>
            </div>
        </div>
    )
}

export default FullscreenImagePopup
